const PREFIX = "sugar-tips-";

const KEYS = {
  enabled: `${PREFIX}enabled`,
  /** After "restart tour", keep autoplaying until "don't show again". */
  onboardingReplay: `${PREFIX}onboarding-replay`,
  tour: (id: string) => `${PREFIX}tour-${id}`,
  tip: (id: string) => `${PREFIX}${id}`,
};

/** Same-tab event so SettingsMenu can sync the usage-tips checkbox. */
export const TIPS_ENABLED_CHANGED_EVENT = "sugar-tips-enabled-changed";

export class TipStorage {
  static isTipsEnabled(): boolean {
    const val = localStorage.getItem(KEYS.enabled);
    return val === null || val === "true";
  }

  static setTipsEnabled(enabled: boolean): void {
    localStorage.setItem(KEYS.enabled, String(enabled));
    if (!enabled) {
      localStorage.removeItem(KEYS.onboardingReplay);
    }
    window.dispatchEvent(
      new CustomEvent(TIPS_ENABLED_CHANGED_EVENT, {
        detail: { enabled },
      }),
    );
  }

  static hasSeenTour(tourId: string): boolean {
    return localStorage.getItem(KEYS.tour(tourId)) === "seen";
  }

  static markTourSeen(tourId: string): void {
    localStorage.setItem(KEYS.tour(tourId), "seen");
  }

  static isOnboardingReplay(): boolean {
    return localStorage.getItem(KEYS.onboardingReplay) === "true";
  }

  /** Keep showing the onboarding tour on each visit until tips are disabled. */
  static setOnboardingReplay(enabled: boolean): void {
    if (enabled) {
      localStorage.setItem(KEYS.onboardingReplay, "true");
      localStorage.removeItem(KEYS.tour("onboarding"));
    } else {
      localStorage.removeItem(KEYS.onboardingReplay);
    }
  }

  /**
   * First visit (never completed) or explicit replay after restart —
   * but never when the user chose "don't show again" / disabled tips.
   */
  static shouldAutoStartOnboarding(): boolean {
    if (!this.isTipsEnabled()) return false;
    if (this.isOnboardingReplay()) return true;
    return !this.hasSeenTour("onboarding");
  }

  static hasSeenTip(tipId: string): boolean {
    return localStorage.getItem(KEYS.tip(tipId)) === "seen";
  }

  static markTipSeen(tipId: string): void {
    localStorage.setItem(KEYS.tip(tipId), "seen");
  }

  static resetAll(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}
